import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Eyebrow, MaskText, Reveal } from "@/components/motion";
import { COLLECTIONS, collectionUrl, formatINR } from "@/data/catalog";
import { EASE } from "@/lib/scroll";

const FILTERS = [
  ["all", `All ${COLLECTIONS.length}`],
  ["WINK", "WINK colour"],
  ["Lashes", "Lashes & glue"],
  ["Blenders", "Blenders"],
  ["Brushes", "Brushes"],
];

const Tile = ({ c, i, dim }) => {
  const ref = useRef(null);
  const big = i === 0;
  const onMove = (e) => {
    const el = ref.current;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top) / r.height;
    el.style.setProperty("--mx", `${x * 100}%`);
    el.style.setProperty("--my", `${y * 100}%`);
    el.style.setProperty("--rx", `${(0.5 - y) * 5}deg`);
    el.style.setProperty("--ry", `${(x - 0.5) * 7}deg`);
  };
  const onLeave = () => {
    ref.current.style.setProperty("--rx", "0deg");
    ref.current.style.setProperty("--ry", "0deg");
  };
  return (
    <motion.a
      href={collectionUrl(c.handle)}
      data-testid={`collection-card-${c.slug}`}
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, ease: EASE, delay: (i % 3) * 0.08 }}
      className={`group relative block ${c.span} transition-opacity duration-500 ${dim ? "opacity-25 saturate-0" : "opacity-100"}`}
    >
      <div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} className="bento-inner">
        <img src={c.img} alt={c.name} loading="lazy" className="bento-img" style={{ objectPosition: c.pos, "--z": c.zoom ?? 1, transformOrigin: c.origin ?? "50% 50%" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-black/0" />
        <div className="bento-spot" />
        <div className="absolute left-3 top-3 flex items-center gap-2 sm:left-5 sm:top-5">
          <span className="font-mono text-xs text-cream/90">{String(i + 1).padStart(2, "0")}</span>
          <span className="rounded-full border border-white/20 bg-black/30 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-champagne backdrop-blur-md">{c.line}</span>
        </div>
        <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 p-3 sm:p-5 lg:p-6">
          <div className="min-w-0">
            <h3 className={`font-display leading-[0.95] text-cream ${big ? "text-4xl sm:text-5xl lg:text-6xl" : "text-[1.35rem] sm:text-3xl"}`}>{c.name}</h3>
            {c.sub && <p className="mt-1 text-xs uppercase tracking-[0.2em] text-champagne/90">{c.sub}</p>}
            <p className="mt-2 line-clamp-1 font-mono text-xs text-stone">{c.tag}</p>
            <p className="mt-1 text-xs text-cream/80" data-testid={`collection-card-price-${c.slug}`}>From {formatINR(c.from)}</p>
          </div>
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-champagne/40 text-champagne transition-all duration-500 group-hover:rotate-45 group-hover:bg-champagne group-hover:text-ink sm:h-11 sm:w-11">
            <ArrowUpRight className="h-4 w-4" />
          </span>
        </div>
      </div>
    </motion.a>
  );
};

export const CollectionsBento = () => {
  const [filter, setFilter] = useState("all");
  return (
    <section id="collections" data-testid="collections-section" className="relative mx-auto max-w-[1440px] px-5 py-24 sm:px-8 lg:px-12 lg:py-36">
      <div className="mb-12 grid gap-8 lg:mb-16 lg:grid-cols-12 lg:items-end">
        <div className="lg:col-span-7">
          <Eyebrow>01 — The Edit</Eyebrow>
          <h2 className="mt-5 font-display text-5xl font-light leading-[0.95] text-cream sm:text-6xl lg:text-7xl">
            <MaskText lines={["Eleven collections.", <em key="e" className="italic text-champagne">One clear path.</em>]} />
          </h2>
        </div>
        <Reveal className="lg:col-span-5" delay={0.15}>
          <p className="text-sm leading-relaxed text-stone sm:text-base">
            Every shade, lash and tool from the Beauty Edit, arranged the way you shop: colour first, then lashes, then the blenders and brushes that finish the look. Tap a collection to open it in the store.
          </p>
          <div className="mt-6 flex flex-wrap gap-2" role="tablist">
            {FILTERS.map(([key, label]) => (
              <button
                key={key}
                role="tab"
                aria-selected={filter === key}
                data-testid={`collections-filter-${key.toLowerCase()}`}
                onClick={() => setFilter(key)}
                className={`rounded-full border px-4 py-2 text-xs uppercase tracking-[0.18em] transition-colors duration-300 ${filter === key ? "border-champagne bg-champagne text-ink" : "border-white/15 text-cream hover:border-champagne/60"}`}
              >
                {label}
              </button>
            ))}
          </div>
        </Reveal>
      </div>
      <div data-testid="collections-bento-grid" className="grid auto-rows-[190px] grid-cols-2 gap-3 sm:auto-rows-[220px] sm:gap-4 md:grid-cols-6 lg:auto-rows-[250px] lg:grid-cols-12">
        {COLLECTIONS.map((c, i) => (
          <Tile key={c.slug} c={c} i={i} dim={filter !== "all" && c.line !== filter} />
        ))}
      </div>
    </section>
  );
};
